import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { useCanAdmin } from "@/lib/useCanAdmin";
import { toast } from "sonner";
import {
  Receipt, CheckSquare, Square, Warning, ArrowClockwise,
  MapPin, CaretLeft, CaretRight, MagnifyingGlass, Truck,
} from "@phosphor-icons/react";

const STATUS_LABELS = {
  em_desenvolvimento: "Em Desenvolvimento",
  separacao: "Separação",
  gravacao_tela: "Gravação de Tela",
  serigrafia: "Serigrafia",
  esteira: "Esteira",
  expedicao: "Expedição",
  enviado: "Enviado",
};

/**
 * Fiscal · Emissão
 * Lista os pedidos que AINDA não têm NF-e ativa (nem no PopCopos nem no
 * Bling — a checagem se baseia na coleção local `nfes`). Paginação
 * server-side (50/pg), busca por nº pedido / cliente / nº pedido na loja
 * mantida entre as páginas. Emissão individual ou em lote com % de redução.
 */
export default function FiscalEmit() {
  const { canAdmin } = useCanAdmin();

  // ------------ Listagem paginada ------------
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // ------------ Busca (debounced) ------------
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const debounceRef = useRef(null);

  const onSearchChange = (v) => {
    setSearchInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(v.trim());
    }, 300);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fiscal/emit-candidates", {
        params: { page, page_size: pageSize, search: search || undefined },
      });
      setOrders(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      toast.error("Falha ao carregar pedidos: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => { load(); }, [load]);

  // ------------ Configuração fiscal (checa natureza da operação) ------------
  const [fiscalCfg, setFiscalCfg] = useState(null);
  useEffect(() => {
    api.get("/fiscal/config").then(({ data }) => setFiscalCfg(data)).catch(() => {});
  }, []);
  const missingNaturezaOperacao = fiscalCfg && !fiscalCfg.natureza_operacao_id;

  // ------------ Seleção em lote ------------
  const [selected, setSelected] = useState(new Set());
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    const ids = orders.map((o) => o.id);
    const allChecked = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  // ------------ Modais ------------
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("single"); // "single" | "batch"
  const [modalOrder, setModalOrder] = useState(null);
  const [percentage, setPercentage] = useState(100);
  const [submitting, setSubmitting] = useState(false);

  const openSingle = (order) => {
    setModalMode("single");
    setModalOrder(order);
    setPercentage(100);
    setShowModal(true);
  };
  const openBatch = () => {
    if (selected.size === 0) { toast.error("Selecione ao menos 1 pedido"); return; }
    setModalMode("batch");
    setModalOrder(null);
    setPercentage(100);
    setShowModal(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const factor = Math.max(0.01, Math.min(1.0, percentage / 100));
      if (modalMode === "single") {
        const { data } = await api.post("/fiscal/nfe/emit-from-order", {
          order_id: modalOrder.id,
          apply_percentage: factor,
        });
        if (data.already_exists) toast.warning("Este pedido já possui NF-e — veja o Histórico");
        else toast.success("NF-e enviada ao Bling — acompanhe no Histórico (status atualiza automaticamente)");
      } else {
        const { data } = await api.post("/fiscal/nfe/emit-batch", {
          order_ids: Array.from(selected),
          apply_percentage: factor,
        });
        const started = (data.results || []).filter((r) => r.ok && !r.already_exists).length;
        const already = (data.results || []).filter((r) => r.already_exists).length;
        toast.success(`Lote iniciado: ${started} em processamento · ${already} já existiam · ${data.fail} falha`);
        if (data.fail > 0) {
          const first = (data.results || []).find((r) => !r.ok);
          if (first) toast.error(`Ex.: ${first.error}`);
        }
        setSelected(new Set());
      }
      setShowModal(false);
      await load();
    } catch (e) {
      toast.error("Falha ao emitir: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  // ------------ Ações admin ------------
  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    if (backfilling) return;
    if (!confirm("Vou varrer os pedidos importados sem UF e preencher o endereço a partir do cadastro de Clientes (e do Bling, se disponível). Continuar?")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/orders/maintenance/backfill-customer-addresses", null, {
        params: { dry_run: false, fetch_from_bling: true },
        timeout: 300000,
      });
      const done = (data.filled_from_local_cache || 0) + (data.filled_from_bling_api || 0);
      toast.success(`${done} pedidos atualizados. ${data.still_missing} ainda sem UF.`);
      await load();
    } catch (e) {
      toast.error("Falha: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBackfilling(false);
    }
  };

  const [movingToShipped, setMovingToShipped] = useState(false);
  const moveEmittedToShipped = async () => {
    if (movingToShipped) return;
    // Dry-run pra mostrar quantos serão movidos
    try {
      const preview = await api.post("/fiscal/kanban/move-emitted-to-shipped", { dry_run: true });
      const n = preview.data.candidates || 0;
      if (n === 0) { toast.info("Nenhum pedido com NF-e autorizada pra mover"); return; }
      if (!confirm(`${n} pedido(s) com NF-e autorizada serão movidos para a coluna "Enviado". Continuar?`)) return;
    } catch (e) {
      toast.error("Falha ao verificar: " + (e?.response?.data?.detail || e.message));
      return;
    }
    setMovingToShipped(true);
    try {
      const { data } = await api.post("/fiscal/kanban/move-emitted-to-shipped", { dry_run: false });
      toast.success(`${data.moved} pedido(s) movidos para Enviado`);
      await load();
    } catch (e) {
      toast.error("Falha: " + (e?.response?.data?.detail || e.message));
    } finally {
      setMovingToShipped(false);
    }
  };

  const missingUfCount = useMemo(
    () => orders.filter((o) => !(o.customer_address?.state || "").trim()).length,
    [orders],
  );

  const allOnPageSelected = orders.length > 0 && orders.every((o) => selected.has(o.id));

  return (
    <div className="space-y-4">
      {missingNaturezaOperacao && (
        <div className="border border-red-600 bg-red-50 p-4 flex items-start gap-3" data-testid="fiscal-emit-missing-natureza">
          <span className="text-xl">⚠️</span>
          <div className="flex-1 text-sm">
            <div className="font-bold text-red-800">Configure a Natureza da Operação antes de emitir</div>
            <div className="text-red-700 mt-1">
              Sem o <b>ID da Natureza da Operação</b>, o Bling rejeita a NF-e com "Alguns dados informados não são válidos". Como resolver em 30 segundos:
            </div>
            <ol className="text-red-700 mt-2 pl-5 list-decimal space-y-1">
              <li>Entre no seu Bling → <b>Cadastros → Naturezas de operação</b> → escolha ou crie uma (ex.: "Venda de mercadoria adquirida ou recebida de terceiros") e anote o ID</li>
              <li>Volte aqui, vá em <b>Fiscal → Configurações</b> → cole o ID no campo <b>"ID Natureza da Operação (Bling)"</b> → Salvar</li>
              <li>Tente emitir a NF-e novamente</li>
            </ol>
            <a href="/fiscal/config" className="inline-block mt-3 px-3 py-1.5 bg-red-600 text-white text-xs font-bold hover:bg-red-700" data-testid="fiscal-emit-goto-config">Ir para Configurações →</a>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[280px]">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="fiscal-emit-search"
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar por nº pedido, cliente ou nº na loja…"
              className="border border-black pl-8 pr-3 py-2 text-sm w-full"
            />
          </div>
          <button onClick={load} className="p-2 border border-black" title="Recarregar" data-testid="fiscal-emit-reload">
            <ArrowClockwise size={16} weight="bold" />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canAdmin && missingUfCount > 0 && (
            <button
              onClick={runBackfill}
              disabled={backfilling}
              className="px-3 py-2 border border-yellow-500 bg-yellow-50 hover:bg-yellow-100 text-sm font-medium flex items-center gap-2 disabled:opacity-40"
              title="Preenche endereço/UF dos pedidos antigos"
              data-testid="fiscal-backfill-uf-btn"
            >
              <MapPin size={16} weight="bold" />
              {backfilling ? "Preenchendo…" : `Preencher UF (${missingUfCount})`}
            </button>
          )}
          {canAdmin && (
            <button
              onClick={moveEmittedToShipped}
              disabled={movingToShipped}
              className="px-3 py-2 border border-black bg-white hover:bg-black hover:text-white text-sm font-medium flex items-center gap-2 disabled:opacity-40"
              title="Move para a coluna 'Enviado' todos os pedidos que já têm NF-e autorizada"
              data-testid="fiscal-move-to-shipped-btn"
            >
              <Truck size={16} weight="bold" />
              {movingToShipped ? "Movendo…" : "Mover NF-e emitidas → Enviado"}
            </button>
          )}
          <button
            onClick={openBatch}
            disabled={selected.size === 0}
            className="px-4 py-2 bg-black text-white text-sm font-bold flex items-center gap-2 disabled:opacity-40"
            data-testid="fiscal-emit-batch-btn"
          >
            <Receipt size={16} weight="bold" />
            Emitir em lote ({selected.size})
          </button>
        </div>
      </div>

      {/* Info bar */}
      <div className="text-xs text-zinc-500 flex items-center gap-4 flex-wrap">
        <span data-testid="fiscal-emit-total">
          <b className="text-black font-mono">{total.toLocaleString("pt-BR")}</b> pedido(s) sem NF-e ativa
        </span>
        {search && <span>· filtro: <b>"{search}"</b></span>}
        {selected.size > 0 && (
          <span className="flex items-center gap-2">
            · <b>{selected.size}</b> selecionado(s) no total (inclusive de outras páginas)
            <button
              onClick={() => setSelected(new Set())}
              className="underline text-xs hover:text-black"
              data-testid="fiscal-emit-clear-selection"
            >
              limpar seleção
            </button>
          </span>
        )}
      </div>

      {/* Tabela */}
      <div className="border border-black bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 border-b border-black">
            <tr>
              <th className="p-3 text-left w-10">
                <button onClick={toggleAllOnPage} data-testid="fiscal-emit-select-all" title="Selecionar todos desta página">
                  {allOnPageSelected ? <CheckSquare size={20} weight="fill" /> : <Square size={20} />}
                </button>
              </th>
              <th className="p-3 text-left">Pedido</th>
              <th className="p-3 text-left">Cliente</th>
              <th className="p-3 text-left">Nº na Loja</th>
              <th className="p-3 text-left">UF</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-6 text-center text-zinc-400">Carregando…</td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-zinc-400">
                {search ? `Nenhum pedido encontrado para "${search}"` : "Nenhum pedido pendente de emissão de NF-e"}
              </td></tr>
            )}
            {!loading && orders.map((o) => {
              const uf = o.customer_address?.state || o.customer_address?.uf || "—";
              return (
                <tr key={o.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`fiscal-emit-row-${o.id}`}>
                  <td className="p-3">
                    <button onClick={() => toggleOne(o.id)} data-testid={`fiscal-emit-check-${o.id}`}>
                      {selected.has(o.id) ? <CheckSquare size={18} weight="fill" /> : <Square size={18} />}
                    </button>
                  </td>
                  <td className="p-3 font-mono font-semibold">#{o.bling_number || "—"}</td>
                  <td className="p-3">{o.customer || "—"}</td>
                  <td className="p-3 font-mono text-xs text-zinc-600">{o.store_number || "—"}</td>
                  <td className="p-3 font-mono uppercase">{uf}</td>
                  <td className="p-3 text-right font-mono">{fmtBRL(o.total_value)}</td>
                  <td className="p-3 text-center">
                    <span className="text-[10px] font-mono px-2 py-0.5 uppercase bg-zinc-200">
                      {STATUS_LABELS[o.production_status] || o.production_status || "—"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => openSingle(o)}
                      disabled={missingNaturezaOperacao}
                      title={missingNaturezaOperacao ? "Configure a Natureza da Operação primeiro" : ""}
                      className="px-3 py-1 text-xs border border-black bg-white hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`fiscal-emit-single-${o.id}`}
                    >
                      Emitir NF-e
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {total > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-zinc-500 font-mono">
            Página <b className="text-black">{page}</b> de <b className="text-black">{totalPages}</b>
            {" · "}mostrando {orders.length} de {total.toLocaleString("pt-BR")}
          </div>
          <div className="flex items-center gap-1">
            <PageBtn onClick={() => setPage(1)} disabled={page <= 1} testid="fiscal-page-first">«</PageBtn>
            <PageBtn onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} testid="fiscal-page-prev">
              <CaretLeft size={14} weight="bold" />
            </PageBtn>
            <span className="px-3 py-1 border border-black bg-white font-mono text-sm">{page}</span>
            <PageBtn onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} testid="fiscal-page-next">
              <CaretRight size={14} weight="bold" />
            </PageBtn>
            <PageBtn onClick={() => setPage(totalPages)} disabled={page >= totalPages} testid="fiscal-page-last">»</PageBtn>
          </div>
        </div>
      )}

      {/* Modal Emissão */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !submitting && setShowModal(false)}>
          <div className="bg-white border border-black w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="fiscal-emit-modal">
            <div className="border-b border-black px-5 py-4">
              <div className="overline text-zinc-500 text-xs">
                {modalMode === "single" ? "Emitir NF-e" : `Emissão em lote (${selected.size} pedidos)`}
              </div>
              <div className="font-head font-black text-lg">
                {modalMode === "single" ? `Pedido #${modalOrder?.bling_number}` : "Lote"}
              </div>
              {modalMode === "single" && (
                <div className="text-xs text-zinc-500 mt-1">{modalOrder?.customer}</div>
              )}
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wide text-zinc-600 mb-2">Valor a emitir na nota</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number" min={1} max={100} step={1}
                    value={percentage}
                    onChange={(e) => setPercentage(Number(e.target.value))}
                    className="border border-black px-3 py-2 w-24 font-mono text-right"
                    data-testid="fiscal-emit-percentage"
                  />
                  <span className="text-sm text-zinc-500">% do valor unitário do pedido</span>
                </div>
                <div className="mt-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 p-2 flex gap-2">
                  <Warning size={16} className="shrink-0 mt-0.5" />
                  <span>O pedido no PopCopos <b>não</b> é alterado. Apenas a NF-e vai com o valor reduzido. 100% = valor cheio.</span>
                </div>
              </div>
            </div>
            <div className="border-t border-black px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setShowModal(false)} disabled={submitting} className="px-4 py-2 text-sm border border-black hover:bg-zinc-100" data-testid="fiscal-emit-modal-cancel">Cancelar</button>
              <button onClick={submit} disabled={submitting} className="px-4 py-2 text-sm bg-black text-white font-bold hover:bg-zinc-800 disabled:opacity-50" data-testid="fiscal-emit-modal-confirm">
                {submitting ? "Enviando ao Bling…" : "Emitir NF-e"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PageBtn({ children, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="px-3 py-1 border border-black hover:bg-black hover:text-white text-sm font-mono disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-black"
    >
      {children}
    </button>
  );
}
